import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  AtSign, Send, BookOpen, Users, Repeat2, ExternalLink, Check, Loader2,
  RotateCcw, Gift, Timer, Users2, Radio, Link2, Trophy, UserPlus, Share2, Flame, ArrowRight, Sparkles, Pickaxe,
} from "lucide-react";
import { toast } from "sonner";
import Reveal, { Eyebrow, EASE } from "@/components/Reveal";
import { useWallet } from "@/lib/wallet";
import WalletButton from "@/components/WalletButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_CONFIG = { pool: "1,000,000", ends_at: "2026-11-12T23:59:59Z", referral_pool: "100,000", conversion_day: "2026-11-18" };
const REF_KEY = "bwpay_ref";
const REFERRAL_REWARD = 100;
const X_POST = encodeURIComponent(
  "Exploring @bnwpay — non-custodial USDC payments on Solana. Tag 3 friends who should accept stablecoins"
);

const TASKS = [
  { id: "follow-twitter", icon: AtSign, title: "Follow us on X", desc: "Follow @bnwpay on X (Twitter).", points: 100, href: "https://x.com/bnwpay", cta: "Open X", proof: false },
  { id: "join-telegram", icon: Send, title: "Join Telegram", desc: "Join the B&W Pay Telegram community for updates and support.", points: 100, href: "https://t.me/bnwpayofficial", cta: "Open Telegram", proof: false },
  { id: "join-medium", icon: BookOpen, title: "Join Medium", desc: "Follow B&W Pay on Medium for protocol deep-dives and announcements.", points: 100, href: "https://medium.com/@bnwpay", cta: "Open Medium", proof: false },
  { id: "tag-3-people", icon: Users, title: "Tag 3 people", desc: "Tag 3 friends under our latest airdrop post on X.", points: 150, href: `https://x.com/intent/post?text=${X_POST}`, cta: "Compose post", proof: true },
  { id: "retweet-5-posts", icon: Repeat2, title: "Retweet last 5 posts", desc: "Retweet the last 5 posts from @bnwpay on X.", points: 150, href: "https://x.com/bnwpay", cta: "Open posts", proof: true },
];

const TOTAL = TASKS.reduce((s, t) => s + t.points, 0);
const short = (w) => `${w.slice(0, 6)}…${w.slice(-4)}`;
const ago = (iso) => {
  if (!iso) return "—";
  const m = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

function useCountdown(endsAt) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, []);
  const diff = Math.max(0, new Date(endsAt).getTime() - now);
  return {
    d: Math.floor(diff / 86400000),
    h: Math.floor((diff / 3600000) % 24),
    m: Math.floor((diff / 60000) % 60),
    s: Math.floor((diff / 1000) % 60),
  };
}

export default function AirdropPortal() {
  const { address } = useWallet();
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [ps, setPs] = useState(null); // live presale pricing — single source for the CTA
  const [mission, setMission] = useState(null); // today's trend prompt + compose url
  const [dailyBusy, setDailyBusy] = useState(false);
  const [dailyProof, setDailyProof] = useState("");
  const [me, setMe] = useState({ tasks: [], proofs: {}, points: 0, claimed: false, referral_count: 0, referral_bonus: 0, referral_link: "" });
  const [live, setLive] = useState({ participants: null, total_points: 0, wallets: [] });
  const [miningLive, setMiningLive] = useState(null);
  const [verifying, setVerifying] = useState(null);
  const [proofInputs, setProofInputs] = useState({});
  const { d, h, m, s } = useCountdown(config.ends_at);

  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref && ref.length >= 32) localStorage.setItem(REF_KEY, ref);
  }, []);

  const loadMe = useCallback(async (wallet) => {
    try {
      const r = await fetch(`${API}/airdrop/participant?wallet=${wallet}`);
      if (r.ok) setMe(await r.json());
    } catch (e) {}
  }, []);

  const loadLive = useCallback(async () => {
    try {
      const r = await fetch(`${API}/airdrop/live`);
      if (r.ok) setLive(await r.json());
    } catch (e) {}
  }, []);

  useEffect(() => {
    fetch(`${API}/airdrop/config`)
      .then((r) => r.json())
      .then(setConfig)
      .catch(() => {});
    fetch(`${API}/presale/stats`)
      .then((r) => r.json())
      .then(setPs)
      .catch(() => {});
    fetch(`${API}/airdrop/daily`)
      .then((r) => r.json())
      .then(setMission)
      .catch(() => {});
    fetch(`${API}/mining/live`)
      .then((r) => r.json())
      .then(setMiningLive)
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (address) loadMe(address);
    else setMe({ tasks: [], proofs: {}, points: 0, claimed: false, referral_count: 0, referral_bonus: 0, referral_link: "" });
  }, [address, loadMe]);

  useEffect(() => {
    loadLive();
    const iv = setInterval(loadLive, 5000);
    return () => clearInterval(iv);
  }, [loadLive]);

  const doneCount = me.tasks.length;
  const earned = me.points;
  const allDone = doneCount === TASKS.length;
  const fullyDone = live.wallets.filter((w) => w.tasks >= TASKS.length);
  const goal = TOTAL + me.referral_bonus;

  const prevAllDone = useRef(false);
  useEffect(() => {
    const nowDone = !!address && doneCount === TASKS.length;
    if (nowDone && !prevAllDone.current) {
      toast.success("Thank you For Participation.", { description: "All 5 quests verified — 600 BWXP reserved" });
    }
    prevAllDone.current = nowDone;
  }, [doneCount, address]);

  const verify = async (task) => {
    if (!address) {
      toast.error("Connect your wallet first to participate");
      return;
    }
    if (me.tasks.includes(task.id)) return;
    if (task.proof) {
      const link = (proofInputs[task.id] || "").trim();
      if (link.length < 8) {
        setProofInputs((p) => ({ ...p, [task.id]: p[task.id] || "" }));
        toast.info("Open X, make your post, then paste its link here");
        return;
      }
      await submit(task, link);
      return;
    }
    await submit(task, "");
  };

  const submit = async (task, proof) => {
    setVerifying(task.id);
    try {
      const r = await fetch(`${API}/airdrop/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ wallet: address, task_id: task.id, proof, ref: localStorage.getItem(REF_KEY) || "" }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Verification failed");
      setMe((prev) => ({ ...prev, tasks: d.tasks, proofs: d.proofs, points: d.points, joined: true }));
      if (d.referred_by) localStorage.removeItem(REF_KEY);
      toast.success("Task verified on the server", { description: `+${task.points} BWXP recorded for ${short(address)}` });
      loadMe(address);
      loadLive();
    } catch (e) {
      toast.error(e?.message || "Verification failed");
    }
    setVerifying(null);
  };

  const copyText = (text, msg) => {
    const fallback = () => {
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        toast.success(msg);
      } catch (e) {
        toast.error("Copy failed — select and copy manually");
      }
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(() => toast.success(msg), fallback);
    } else {
      fallback();
    }
  };

  const claim = async () => {
    if (!address) {
      toast.error("Connect your wallet first");
      return;
    }
    try {
      const r = await fetch(`${API}/airdrop/claim`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ wallet: address }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Could not claim");
      setMe((prev) => ({ ...prev, claimed: true }));
      toast.success(d.already ? "Already claimed" : `${me.points} BWXP claimed`, { description: `Allocation reserved for ${short(address)}` });
      loadLive();
    } catch (e) {
      toast.error(e?.message || "Could not claim");
    }
  };

  const dailyClaim = async () => {
    if (!address) {
      toast.error("Connect your wallet first");
      return;
    }
    if ((dailyProof || "").trim().length < 8) {
      toast.info("Post today's $BNW mission on X, then paste your post link here");
      return;
    }
    setDailyBusy(true);
    try {
      const r = await fetch(`${API}/airdrop/daily`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ wallet: address, proof: dailyProof.trim() }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Could not record today's mission");
      setMe((prev) => ({ ...prev, daily: d.daily, daily_points: d.daily_points, daily_done_today: true, points: d.points }));
      setDailyProof("");
      toast.success("+50 BWXP — $BNW is trending thanks to you", { description: "Next mission unlocks at 00:00 UTC" });
      loadLive();
    } catch (e) {
      toast.error(e?.message || "Could not record today's mission");
    }
    setDailyBusy(false);
  };

  const reset = async () => {
    if (!address) return;
    try {
      await fetch(`${API}/airdrop/reset`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ wallet: address }),
      });
      setMe({ tasks: [], proofs: {}, daily: [], daily_points: 0, daily_done_today: false, daily_reward: 50, points: 0, claimed: false, referral_count: 0, referral_bonus: 0, referral_link: "" });
      toast.info("Progress cleared on the server");
      loadLive();
    } catch (e) {
      toast.error("Reset failed");
    }
  };

  const shareUrl = me.referral_link || (address ? `${window.location.origin}/airdrop?ref=${address}` : "");
  const shareIntent = `https://x.com/intent/post?text=${encodeURIComponent(
    "Join the @bnwpay airdrop — non-custodial USDC payments on Solana"
  )}&url=${encodeURIComponent(shareUrl)}`;

  const _now = new Date();
  const nextMidnight = new Date(Date.UTC(_now.getUTCFullYear(), _now.getUTCMonth(), _now.getUTCDate() + 1)).toISOString();
  const nextDay = useCountdown(nextMidnight);

  return (
    <main className="relative min-h-screen overflow-hidden pt-[72px]" data-testid="airdrop-page">
      <div className="bg-grid absolute inset-0" />
      <div className="animate-glow pointer-events-none absolute -top-32 left-1/2 h-[480px] w-[720px] -translate-x-1/2 rounded-full bg-sgreen/10 blur-[140px]" />

      <div className="relative mx-auto max-w-4xl px-4 py-20 sm:px-6 lg:px-12">
        <Reveal className="text-center">
          <Eyebrow>B&W Pay Airdrop · Live</Eyebrow>
          <h1 className="mx-auto mt-5 max-w-2xl text-4xl font-bold tracking-[-0.02em] sm:text-5xl lg:text-6xl">
            Complete quests.{" "}
            <span className="text-sgreen">Earn BWXP.</span>
          </h1>
          <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-mut sm:text-lg">
            Five quests plus a daily mission — post with $BNW every day and make it trend on X.
            Every point converts 100:1 into $BNW on conversion day, 18 November.
          </p>
        </Reveal>

        <Reveal delay={0.08}>
          <div className="mt-12 rounded-2xl border border-sgreen/25 bg-sgreen/[0.05] p-6 text-center sm:p-8" data-testid="airdrop-pool-card">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-mut">Reward pool</p>
            <p className="mt-3 font-mono text-4xl font-bold tracking-tight text-white sm:text-5xl" data-testid="airdrop-pool-amount">
              {config.pool} <span className="text-xl text-sgreen sm:text-2xl">BWXP</span>
            </p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 font-mono text-xs text-mut">
              <span className="inline-flex items-center gap-2" data-testid="airdrop-participants">
                <Users2 className="h-3.5 w-3.5 text-sgreen" />
                {live.participants === null ? "—" : `${live.participants} wallet${live.participants === 1 ? "" : "s"} joined`}
              </span>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.09}>
          <a
            href="/mining"
            data-testid="mining-banner"
            className="group relative mt-6 flex flex-col gap-4 overflow-hidden rounded-2xl border border-sgreen/40 bg-sgreen/[0.07] p-5 transition-all duration-500 hover:bg-sgreen/[0.12] sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="animate-glow pointer-events-none absolute -right-16 -top-20 h-52 w-52 rounded-full bg-sgreen/25 blur-[70px]" />
            <div className="flex min-w-0 items-start gap-4">
              <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-sgreen text-black">
                <Pickaxe className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-sgreen" data-testid="mining-banner-seats">
                  Free · no subscription{miningLive?.tiers?.free ? ` · ${miningLive.tiers.free.remaining.toLocaleString()} seats left` : ""}
                </p>
                <p className="mt-1.5 text-base font-bold text-white sm:text-lg" data-testid="mining-banner-headline">
                  Mine Real USDC Daily with B&W, Absolutely Free
                </p>
                <p className="mt-1 text-sm leading-relaxed text-mut">
                  Your airdrop wallet already qualifies. Switch the rig on and USDC lands every 24 hours for 45 days.
                </p>
              </div>
            </div>
            <span className="cta-glow inline-flex shrink-0 items-center gap-2 self-start rounded-full bg-sgreen px-6 py-3 text-sm font-semibold text-black sm:self-center">
              Start Mining
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </span>
          </a>
        </Reveal>

        <Reveal delay={0.1}>
          <a
            href="/presale"
            data-testid="presale-banner"
            className="group mt-6 flex flex-col gap-4 rounded-2xl border border-sgreen/40 bg-sgreen/[0.07] p-5 transition-all duration-500 hover:bg-sgreen/[0.12] sm:flex-row sm:items-center sm:justify-between"
          >
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-sgreen" data-testid="presale-banner-pricing">
                {ps ? `Presale live · $${ps.price?.toFixed(2)} now → $${ps.listing_price} at listing` : "Presale live · 3 rounds · 250,000 $BNW"}
              </p>
              <p className="mt-1.5 text-base font-semibold text-white sm:text-lg">
                Earning BWXP takes quests. Buying B&W takes 10 seconds
                {ps?.price ? ` — and it's ${(ps.listing_price / ps.price).toFixed(2)}× at listing` : " — listed right after the rounds close"}.
              </p>
            </div>
            <span className="cta-glow inline-flex shrink-0 items-center gap-2 self-start rounded-full bg-sgreen px-6 py-3 text-sm font-semibold text-black sm:self-center">
              Buy Presale
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </span>
          </a>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-6 rounded-2xl border border-line bg-card2 p-6 sm:p-7" data-testid="airdrop-countdown">
            <div className="flex items-center justify-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-mut">
              <Timer className="h-3.5 w-3.5 text-sgreen" />
              Ends in
            </div>
            <div className="mt-4 grid grid-cols-4 gap-3" data-testid="airdrop-countdown-timer">
              {[
                [d, "Days"],
                [h, "Hrs"],
                [m, "Min"],
                [s, "Sec"],
              ].map(([v, l]) => (
                <div key={l} className="rounded-xl border border-line bg-panel py-4 text-center">
                  <p className="font-mono text-2xl font-semibold text-white sm:text-3xl">{String(v).padStart(2, "0")}</p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-mut">{l}</p>
                </div>
              ))}
            </div>
            <p className="mt-4 text-center font-mono text-[11px] text-mut">
              {new Date(config.ends_at).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })} UTC · closes{" "}
              {new Date(config.ends_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
            </p>
            <p className="mt-2 text-center font-mono text-[11px] leading-relaxed text-mut" data-testid="airdrop-release-note">
              100 BWXP = 1 $BNW · BWXP converts on{" "}
              {new Date(`${config.conversion_day || "2026-11-18"}T00:00:00Z`).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })} (conversion day) ·
              redeemable from TGE · 50% claimable at TGE, 50% over the following 2 months
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.12}>
          <div className="mt-6 flex flex-col items-center gap-4 rounded-2xl border border-line bg-card2 p-6 sm:flex-row sm:justify-between" data-testid="airdrop-wallet-card">
            <div>
              <p className="text-sm font-medium text-white">
                {address ? "Wallet connected — progress is saved server-side" : "Connect your wallet to participate"}
              </p>
              <p className="mt-1 font-mono text-xs text-mut" data-testid="airdrop-wallet-address">
                {address ? `${address.slice(0, 10)}…${address.slice(-6)}` : "Phantom and Solflare are supported"}
              </p>
            </div>
            <WalletButton variant="solid" />
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-6 rounded-2xl border border-line bg-card2 p-6 sm:p-7" data-testid="airdrop-progress-card">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="font-mono text-sm text-white" data-testid="airdrop-progress-text">
                {address
                  ? `${earned} / ${goal} BWXP earned${me.referral_bonus ? ` (incl. +${me.referral_bonus} referral bonus)` : ""}`
                  : "Connect your wallet to track progress"}
              </p>
              <p className="font-mono text-xs text-mut">
                {address ? (allDone ? "All quests complete" : `${TASKS.length - doneCount} tasks remaining`) : "0/5 tasks"}
              </p>
            </div>
            <div className="mt-4 h-2 overflow-hidden rounded-full bg-white/[0.06]">
              <motion.div
                data-testid="airdrop-progress-bar"
                className="h-full rounded-full bg-sgreen"
                initial={{ width: 0 }}
                animate={{ width: `${Math.min(100, (earned / Math.max(goal, 1)) * 100)}%` }}
                transition={{ duration: 0.9, ease: EASE }}
              />
            </div>
            {address && (
              <div className="mt-6 flex justify-end">
                <button
                  onClick={reset}
                  data-testid="airdrop-reset-button"
                  className="inline-flex items-center gap-2 rounded-full border border-line px-5 py-2.5 font-mono text-xs text-mut transition-colors hover:border-white/30 hover:text-white"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  Reset
                </button>
              </div>
            )}
          </div>
        </Reveal>

        <Reveal delay={0.04}>
          <div
            className="mt-6 rounded-2xl border border-sgreen/35 bg-sgreen/[0.045] p-6 sm:p-7"
            data-testid="daily-mission-card"
          >
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex items-start gap-4">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-sgreen text-black">
                  <Flame className="h-5 w-5" />
                </span>
                <div>
                  <h3 className="text-lg font-semibold leading-snug text-white">
                    Daily mission — make $BNW trend
                    <span className="ml-2.5 inline-block whitespace-nowrap rounded-full bg-sgreen px-2.5 py-0.5 align-middle font-mono text-[10px] uppercase tracking-[0.08em] text-black">
                      +{me.daily_reward || 50} BWXP every day
                    </span>
                  </h3>
                  <p className="mt-2 max-w-xl text-sm leading-relaxed text-mut">
                    Post on X about $BNW every single day until the airdrop closes on 12 November.
                    One verified post per day (UTC) — every post pushes $BNW up the trending charts.
                  </p>
                </div>
              </div>
              <div className="text-right font-mono text-xs">
                <p className="text-mut">earned from trending</p>
                <p className="text-lg font-bold text-sgreen" data-testid="daily-points-earned">
                  {me.daily_points || 0} BWXP
                </p>
              </div>
            </div>

            {mission && (
              <div className="mt-5 rounded-xl border border-line bg-panel p-4">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-mut">today's prompt</p>
                <p className="mt-2 text-sm leading-relaxed text-white/90" data-testid="daily-prompt">
                  "{mission.prompt}"
                </p>
                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <a
                    href={mission.compose_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="daily-compose-button"
                    className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 font-mono text-xs font-semibold text-black transition-colors hover:bg-white/85"
                  >
                    Compose today's post
                  </a>
                  {address && !me.daily_done_today && (
                    <>
                      <input
                        value={dailyProof}
                        onChange={(e) => setDailyProof(e.target.value)}
                        placeholder="Paste your post link (x.com/...)"
                        data-testid="daily-proof-input"
                        className="min-w-0 flex-1 rounded-full border border-line bg-card2 px-4 py-2 font-mono text-xs text-white placeholder:text-mut focus:border-sgreen/50 focus:outline-none"
                      />
                      <button
                        onClick={dailyClaim}
                        disabled={dailyBusy}
                        data-testid="daily-claim-button"
                        className="inline-flex items-center gap-2 rounded-full bg-sgreen px-4 py-2 font-mono text-xs font-semibold text-black transition-opacity disabled:opacity-50"
                      >
                        {dailyBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                        Claim +{me.daily_reward || 50} BWXP
                      </button>
                    </>
                  )}
                  {!address && (
                    <span className="font-mono text-xs text-mut" data-testid="daily-connect-hint">
                      Connect your wallet to start today's mission
                    </span>
                  )}
                </div>
                {me.daily_done_today && (
                  <p className="mt-3 font-mono text-xs text-sgreen" data-testid="daily-done-note">
                    Today's mission is done — next one unlocks in {String(nextDay.h).padStart(2, "0")}h {String(nextDay.m).padStart(2, "0")}m {String(nextDay.s).padStart(2, "0")}s (00:00 UTC)
                  </p>
                )}
              </div>
            )}

            <p className="mt-4 font-mono text-[11px] leading-relaxed text-mut" data-testid="daily-schedule-note">
              Daily missions run until 12 Nov 2026 — then the airdrop closes. 100 BWXP = 1 $BNW · BWXP
              converts on 18 Nov 2026 (conversion day) and is redeemable from TGE · 50% claimable at TGE,
              50% releases over the following 2 months.
            </p>
          </div>
        </Reveal>

        <div className="mt-6 space-y-4">
          {TASKS.map((t, i) => {
            const done = me.tasks.includes(t.id);
            const busy = verifying === t.id;
            const showProof = t.proof && !done && proofInputs[t.id] !== undefined;
            return (
              <Reveal key={t.id} delay={i * 0.06}>
                <div
                  data-testid={`airdrop-task-${t.id}`}
                  className={`card-hover flex flex-col gap-5 rounded-2xl border p-6 sm:flex-row sm:items-center sm:p-7 ${
                    done ? "border-sgreen/30 bg-sgreen/[0.04]" : "border-line bg-card2"
                  }`}
                >
                  <div
                    className={`inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border ${
                      done ? "border-sgreen/40 bg-sgreen/10" : "border-line bg-white/[0.03]"
                    }`}
                  >
                    <t.icon className="h-5 w-5 text-sgreen" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-3">
                      <h3 className="text-base font-semibold text-white sm:text-lg">{t.title}</h3>
                      <span className="whitespace-nowrap rounded-full border border-sgreen/25 bg-sgreen/10 px-2.5 py-0.5 font-mono text-[10px] font-medium text-sgreen">
                        +{t.points} BWXP
                      </span>
                      {done && (
                        <span
                          data-testid={`airdrop-done-chip-${t.id}`}
                          className="inline-flex items-center gap-1.5 rounded-full bg-sgreen px-2.5 py-0.5 font-mono text-[10px] font-semibold text-black"
                        >
                          <Check className="h-3 w-3" /> Verified
                        </span>
                      )}
                    </div>
                    <p className="mt-1.5 text-sm leading-relaxed text-mut">{t.desc}</p>
                    {showProof && (
                      <div className="mt-3 flex flex-col gap-2 sm:flex-row" data-testid={`airdrop-proof-row-${t.id}`}>
                        <input
                          value={proofInputs[t.id] || ""}
                          onChange={(e) => setProofInputs((p) => ({ ...p, [t.id]: e.target.value }))}
                          placeholder="https://x.com/you/status/…"
                          data-testid={`airdrop-proof-input-${t.id}`}
                          className="w-full rounded-lg border border-line bg-panel px-3.5 py-2.5 font-mono text-xs text-white outline-none focus:border-sgreen/60"
                        />
                        <button
                          onClick={() => verify(t)}
                          disabled={busy}
                          data-testid={`airdrop-proof-submit-${t.id}`}
                          className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-sgreen px-5 py-2.5 text-xs font-semibold text-black disabled:opacity-60"
                        >
                          {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                          Submit proof
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-3">
                    <a
                      href={t.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`airdrop-open-${t.id}`}
                      className="inline-flex items-center gap-1.5 rounded-full border border-line px-4 py-2.5 text-xs text-white transition-colors hover:border-white/30"
                    >
                      {t.cta}
                      <ExternalLink className="h-3.5 w-3.5 text-mut" />
                    </a>
                    {done ? (
                      <span className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-sgreen/40 bg-sgreen/10">
                        <Check className="h-4 w-4 text-sgreen" />
                      </span>
                    ) : (
                      <button
                        onClick={() => verify(t)}
                        disabled={busy}
                        data-testid={`airdrop-verify-${t.id}`}
                        className="inline-flex items-center gap-2 rounded-full bg-sgreen px-5 py-2.5 text-xs font-semibold text-black transition-opacity disabled:opacity-60"
                      >
                        {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                        {busy ? "Verifying" : t.proof && proofInputs[t.id] !== undefined ? "Submit" : "Verify"}
                      </button>
                    )}
                  </div>
                </div>
              </Reveal>
            );
          })}

          {/* Referral quest — ongoing, auto-rewarded */}
          <Reveal delay={0.3}>
            <div
              data-testid="referral-card"
              className="rounded-2xl border border-sgreen/30 bg-sgreen/[0.04] p-6 sm:p-7"
            >
              <div className="flex flex-wrap items-center gap-3">
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl border border-sgreen/40 bg-sgreen/10">
                  <UserPlus className="h-5 w-5 text-sgreen" />
                </div>
                <div>
                  <div className="flex flex-wrap items-center gap-3">
                    <h3 className="text-base font-semibold text-white sm:text-lg">Invite friends</h3>
                    <span className="whitespace-nowrap rounded-full border border-sgreen/25 bg-sgreen/10 px-2.5 py-0.5 font-mono text-[10px] font-medium text-sgreen">
                      +{REFERRAL_REWARD} BWXP per friend
                    </span>
                  </div>
                  <p className="mt-1.5 text-sm leading-relaxed text-mut">
                    Share your personal link — you earn {REFERRAL_REWARD} BWXP for every friend who joins
                    through it and verifies any quest.
                  </p>
                </div>
              </div>

              {address ? (
                <div className="mt-5" data-testid="referral-panel">
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <button
                      onClick={() => copyText(shareUrl, "Referral link copied")}
                      data-testid="referral-link-copy"
                      className="group flex flex-1 items-center gap-2.5 rounded-lg border border-line bg-panel px-4 py-3 text-left transition-colors hover:border-sgreen/40"
                    >
                      <Link2 className="h-4 w-4 shrink-0 text-sgreen" />
                      <span className="truncate font-mono text-xs text-white/80">{shareUrl}</span>
                    </button>
                    <a
                      href={shareIntent}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="referral-share-button"
                      className="cta-glow inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-sgreen px-5 py-3 text-xs font-semibold text-black"
                    >
                      <Share2 className="h-3.5 w-3.5" />
                      Share on X
                    </a>
                  </div>
                  <p className="mt-3 font-mono text-xs text-mut" data-testid="referral-count">
                    {me.referral_count} friend{me.referral_count === 1 ? "" : "s"} joined through your link ·{" "}
                    <span className="text-sgreen">+{me.referral_bonus} BWXP</span>
                  </p>
                </div>
              ) : (
                <p className="mt-5 rounded-xl border border-line bg-panel p-4 text-center font-mono text-xs text-mut" data-testid="referral-connect-hint">
                  Connect your wallet to unlock your personal referral link.
                </p>
              )}
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.05}>
          <div className="mt-6 rounded-2xl border border-line bg-card2 p-6 sm:p-7" data-testid="referral-leaderboard">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h3 className="flex items-center gap-2.5 text-lg font-semibold text-white">
                <Trophy className="h-4 w-4 text-sgreen" />
                Referral leaderboard
              </h3>
              <p className="font-mono text-xs text-mut" data-testid="referral-pool-line">
                Bonus pool: <span className="text-sgreen">{config.referral_pool} BWXP</span>
              </p>
            </div>
            {(live.leaderboard || []).length === 0 ? (
              <p className="mt-5 rounded-xl border border-line bg-panel p-6 text-center font-mono text-xs text-mut" data-testid="referral-leaderboard-empty">
                No referrals yet — share your link above to top the board.
              </p>
            ) : (
              <ol className="mt-5 space-y-2" data-testid="referral-leaderboard-rows">
                {live.leaderboard.map((w, i) => (
                  <li
                    key={w.wallet}
                    data-testid={`referral-leaderboard-row-${i}`}
                    className={`flex items-center justify-between gap-4 rounded-xl border px-4 py-3 ${
                      i === 0 ? "border-sgreen/40 bg-sgreen/[0.06]" : "border-line bg-panel"
                    }`}
                  >
                    <span className="flex min-w-0 items-center gap-3 font-mono text-xs text-white" title={w.wallet}>
                      <span
                        className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] ${
                          i < 3 ? "bg-sgreen font-semibold text-black" : "bg-white/[0.06] text-mut"
                        }`}
                      >
                        {i + 1}
                      </span>
                      <span className="truncate">{short(w.wallet)}</span>
                      {address === w.wallet && <span className="shrink-0 text-sgreen">you</span>}
                    </span>
                    <span className="flex shrink-0 items-center gap-3 font-mono text-[11px]">
                      <span className="text-white/70">
                        {w.referrals} invite{w.referrals === 1 ? "" : "s"}
                      </span>
                      <span className="text-sgreen">{w.points} BWXP</span>
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <div className="mt-8 rounded-2xl border border-line bg-card2 p-6 sm:p-7" data-testid="airdrop-live-console">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h3 className="flex items-center gap-2.5 text-lg font-semibold text-white">
                <Radio className="h-4 w-4 text-sgreen" />
                Live participation
                <span className="ping-dot h-1.5 w-1.5 rounded-full bg-sgreen" />
              </h3>
              <p className="font-mono text-xs text-mut" data-testid="airdrop-live-total-points">
                {live.total_points.toLocaleString()} BWXP awarded so far
              </p>
            </div>
            {live.wallets.length === 0 ? (
              <p className="mt-6 rounded-xl border border-line bg-panel p-6 text-center font-mono text-xs text-mut">
                No wallets have joined yet — connect yours and be the first.
              </p>
            ) : (
              <div className="mt-5 space-y-2" data-testid="airdrop-live-list">
                {live.wallets.map((w, i) => (
                  <div
                    key={w.wallet}
                    data-testid={`airdrop-wallet-row-${i}`}
                    className="flex items-center justify-between gap-4 rounded-xl border border-line bg-panel px-4 py-3"
                  >
                    <span className="min-w-0 truncate font-mono text-xs text-white" title={w.wallet}>
                      {short(w.wallet)}
                      {address === w.wallet && <span className="ml-2 text-sgreen">you</span>}
                    </span>
                    <div className="flex shrink-0 items-center gap-4 font-mono text-[11px]">
                      <span className="text-mut">{w.tasks}/5 tasks</span>
                      {!!w.referrals && <span className="hidden text-white/60 sm:inline">+{w.referrals} refs</span>}
                      <span className="text-sgreen">{w.points} BWXP</span>
                      {w.claimed && <span className="hidden rounded-full bg-sgreen/10 px-2 py-0.5 text-sgreen sm:inline">claimed</span>}
                      <span className="hidden text-mut sm:inline">{ago(w.updated_at)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <div className="mt-6 rounded-2xl border border-sgreen/25 bg-sgreen/[0.04] p-6 sm:p-7" data-testid="airdrop-full-list">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h3 className="flex items-center gap-2.5 text-lg font-semibold text-white">
                <Trophy className="h-4 w-4 text-sgreen" />
                Fully participated wallets
              </h3>
              <p className="font-mono text-xs text-mut" data-testid="airdrop-full-count">
                {fullyDone.length} wallet{fullyDone.length === 1 ? "" : "s"} completed all {TASKS.length} quests
              </p>
            </div>
            {fullyDone.length === 0 ? (
              <p className="mt-5 rounded-xl border border-line bg-panel p-6 text-center font-mono text-xs text-mut" data-testid="airdrop-full-empty">
                No wallet has completed all quests yet — finish all five to appear here.
              </p>
            ) : (
              <ol className="mt-5 space-y-2" data-testid="airdrop-full-rows">
                {fullyDone.map((w, i) => (
                  <li
                    key={w.wallet}
                    className="flex items-center justify-between gap-4 rounded-xl border border-sgreen/25 bg-panel px-4 py-3"
                    data-testid={`airdrop-full-row-${i}`}
                  >
                    <span className="flex min-w-0 items-center gap-3 font-mono text-xs text-white" title={w.wallet}>
                      <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-sgreen/15 text-[10px] text-sgreen">
                        {i + 1}
                      </span>
                      <span className="truncate">{short(w.wallet)}</span>
                      {address === w.wallet && <span className="shrink-0 text-sgreen">you</span>}
                    </span>
                    <span className="flex shrink-0 items-center gap-3 font-mono text-[11px]">
                      <Check className="h-3.5 w-3.5 text-sgreen" />
                      <span className="text-sgreen">{w.points} BWXP</span>
                      {w.claimed && <span className="hidden rounded-full bg-sgreen/10 px-2 py-0.5 text-sgreen sm:inline">claimed</span>}
                      <span className="hidden text-mut sm:inline">{ago(w.updated_at)}</span>
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </Reveal>

        {allDone && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="mt-8 rounded-2xl border border-sgreen/40 bg-sgreen/[0.06] p-8 text-center"
            data-testid="airdrop-claim-panel"
          >
            <Gift className="mx-auto h-8 w-8 text-sgreen" />
            <h3 className="mt-4 text-2xl font-semibold text-white" data-testid="airdrop-success-message">
              Thank you For Participation.
            </h3>
            <p className="mt-2 text-sm text-mut">
              {me.claimed
                ? `All five quests verified and your ${me.points} BWXP allocation is reserved for ${short(address)}. Watch the Telegram channel for distribution details.`
                : `All five quests verified — claim your allocation of ${TOTAL} BWXP${me.referral_bonus ? ` + ${me.referral_bonus} referral bonus` : ""}.`}
            </p>
            {!me.claimed && (
              <button
                onClick={claim}
                data-testid="airdrop-claim-button"
                className="cta-glow mt-6 inline-flex items-center gap-2 rounded-full bg-sgreen px-8 py-3.5 text-sm font-semibold text-black"
              >
                <Gift className="h-4 w-4" />
                Claim {me.points} BWXP
              </button>
            )}
          </motion.div>
        )}
      </div>
    </main>
  );
}
